import React, { useState, useEffect, useRef } from 'react';
import { FaSearch, FaPaperPlane, FaImage, FaEllipsisV, FaCheck, FaCheckDouble, FaArrowLeft } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../utils/supabaseClient';
import { authFetch } from '../../utils/authFetch';
import '../../styles/whatsapp/Chats.css';

const Chats = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [canReply, setCanReply] = useState(false);
  const [windowTimeRemaining, setWindowTimeRemaining] = useState(null);
  const [lastIncomingMessageTime, setLastIncomingMessageTime] = useState(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [editingName, setEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const nameInputRef = useRef(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://krmmadhubanibackend.onrender.com';

  // Initial fetch
  useEffect(() => {
    fetchConversations();
  }, []);

  // Supabase Realtime: Subscribe to conversation updates
  useEffect(() => {
    console.log('🔌 Subscribing to conversation updates...');
    
    const conversationChannel = supabase
      .channel('whatsapp_conversations_changes')
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'whatsapp_conversations'
        },
        (payload) => {
          console.log('⚡ Conversation change detected:', payload.eventType);
          // Refresh conversations list when any change occurs
          fetchConversations();
        }
      )
      .subscribe();

    return () => {
      console.log('🔌 Unsubscribing from conversation updates');
      supabase.removeChannel(conversationChannel);
    };
  }, []);

  // Fetch messages when conversation is selected
  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.id);
      markAsRead(selectedConversation.id);
    }
  }, [selectedConversation]);

  // Supabase Realtime: Subscribe to new messages for selected conversation
  useEffect(() => {
    if (!selectedConversation) return;

    console.log(`🔌 Subscribing to messages for conversation ${selectedConversation.id}...`);

    const messageChannel = supabase
      .channel(`whatsapp_messages_${selectedConversation.id}`)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'whatsapp_messages',
          filter: `conversation_id=eq.${selectedConversation.id}`
        },
        (payload) => {
          console.log('⚡ Message change detected:', payload.eventType);
          
          if (payload.eventType === 'INSERT') {
            // ✅ TRUE REAL-TIME: Add new message instantly without re-fetching
            console.log('📩 New message received, adding to state...');
            setMessages(prev => [...prev, payload.new]);
            markAsRead(selectedConversation.id);
            
            // Update 24-hour window if incoming message
            if (payload.new.direction === 'incoming') {
              const msgTime = new Date(payload.new.sent_at);
              setLastIncomingMessageTime(msgTime);
              setCanReply(true);
              setWindowTimeRemaining(24 * 60 * 60 * 1000);
            }
          } else if (payload.eventType === 'UPDATE') {
            // ✅ Update existing message (status changes: sent → delivered → read)
            console.log('📝 Message status updated');
            setMessages(prev => 
              prev.map(msg => msg.id === payload.new.id ? payload.new : msg)
            );
          } else if (payload.eventType === 'DELETE') {
            // Remove deleted message
            setMessages(prev => prev.filter(msg => msg.id !== payload.old.id));
          }
        }
      )
      .subscribe();

    return () => {
      console.log(`🔌 Unsubscribing from messages for conversation ${selectedConversation.id}`);
      supabase.removeChannel(messageChannel);
    };
  }, [selectedConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      
      // ✅ Direct Supabase query - no server API needed
      const { data, error } = await supabase
        .from('whatsapp_conversations')
        .select('*')
        .eq('is_active', true)
        .order('updated_at', { ascending: false });
      
      if (error) throw error;
      
      setConversations(data || []);
      console.log('✅ Fetched conversations from Supabase:', data?.length);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      // ✅ Direct Supabase query - no server API needed
      const { data, error } = await supabase
        .from('whatsapp_messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('sent_at', { ascending: true });
      
      if (error) throw error;
      
      const msgs = data || [];
      setMessages(msgs);
      console.log('✅ Fetched messages from Supabase:', msgs.length);
      
      // Find last incoming message to calculate 24-hour window
      const lastIncoming = msgs
        .filter(msg => msg.direction === 'incoming')
        .sort((a, b) => new Date(b.sent_at) - new Date(a.sent_at))[0];
      
      if (lastIncoming) {
        const lastMsgTime = new Date(lastIncoming.sent_at);
        setLastIncomingMessageTime(lastMsgTime);
        
        // Calculate if within 24-hour window
        const now = new Date();
        const hoursSinceLastMsg = (now - lastMsgTime) / (1000 * 60 * 60);
        const within24Hours = hoursSinceLastMsg < 24;
        
        setCanReply(within24Hours);
        
        if (within24Hours) {
          // Calculate time remaining
          const msRemaining = (24 * 60 * 60 * 1000) - (now - lastMsgTime);
          setWindowTimeRemaining(msRemaining);
        } else {
          setWindowTimeRemaining(null);
        }
      } else {
        // No incoming messages yet - cannot reply
        setCanReply(false);
        setLastIncomingMessageTime(null);
        setWindowTimeRemaining(null);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const markAsRead = async (conversationId) => {
    try {
      // ✅ Direct Supabase update - mark incoming messages as read
      const { error: updateError } = await supabase
        .from('whatsapp_messages')
        .update({
          status: 'read',
          read_at: new Date().toISOString()
        })
        .eq('conversation_id', conversationId)
        .eq('direction', 'incoming')
        .neq('status', 'read');

      if (updateError) throw updateError;

      // Reset unread count in conversation
      const { error: convError } = await supabase
        .from('whatsapp_conversations')
        .update({ unread_count: 0 })
        .eq('id', conversationId);

      if (convError) throw convError;

      // Update local state immediately for instant UI feedback
      setConversations(prev => prev.map(conv => 
        conv.id === conversationId ? { ...conv, unread_count: 0 } : conv
      ));

      console.log('✅ Marked messages as read in Supabase');
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Update window timer every second
  useEffect(() => {
    if (!lastIncomingMessageTime || !canReply) return;

    const interval = setInterval(() => {
      const now = new Date();
      const msRemaining = (24 * 60 * 60 * 1000) - (now - lastIncomingMessageTime);
      
      if (msRemaining <= 0) {
        setCanReply(false);
        setWindowTimeRemaining(null);
      } else {
        setWindowTimeRemaining(msRemaining);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [lastIncomingMessageTime, canReply]);

  // Format window time remaining
  const formatWindowTime = (ms) => {
    if (!ms) return '';
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  // Handle image click to open modal
  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setImageModalOpen(true);
  };

  // Close image modal
  const closeImageModal = () => {
    setImageModalOpen(false);
    setSelectedImage(null);
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 16MB for WhatsApp)
      if (file.size > 16 * 1024 * 1024) {
        alert('File size must be less than 16MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  // Send message (text, image, or document)
  const handleSendMessage = async () => {
    if (!newMessage.trim() && !selectedFile) return;
    if (!canReply) {
      alert('Cannot reply - 24-hour window has expired. Use Bulk Notice to send template messages.');
      return;
    }

    try {
      setSending(true);
      const formData = new FormData();
      
      formData.append('conversationId', selectedConversation.id);
      formData.append('phoneNumber', selectedConversation.phone_number);
      formData.append('message', newMessage);
      
      if (selectedFile) {
        formData.append('file', selectedFile);
      }

      const response = await authFetch(`${API_BASE_URL}/api/whatsapp/send-message`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();
      
      if (data.success) {
        setNewMessage('');
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        // Message will update via Realtime subscription
      } else {
        alert('Failed to send message: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'read':
        return <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-check2-all status-icon read" viewBox="0 0 16 16">
        <path d="M12.354 4.354a.5.5 0 0 0-.708-.708L5 10.293 1.854 7.146a.5.5 0 1 0-.708.708l3.5 3.5a.5.5 0 0 0 .708 0zm-4.208 7-.896-.897.707-.707.543.543 6.646-6.647a.5.5 0 0 1 .708.708l-7 7a.5.5 0 0 1-.708 0"/>
        <path d="m5.354 7.146.896.897-.707.707-.897-.896a.5.5 0 1 1 .708-.708"/>
      </svg>
      case 'delivered':
        return <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-check2-all status-icon delivered" viewBox="0 0 16 16">
        <path d="M12.354 4.354a.5.5 0 0 0-.708-.708L5 10.293 1.854 7.146a.5.5 0 1 0-.708.708l3.5 3.5a.5.5 0 0 0 .708 0zm-4.208 7-.896-.897.707-.707.543.543 6.646-6.647a.5.5 0 0 1 .708.708l-7 7a.5.5 0 0 1-.708 0"/>
        <path d="m5.354 7.146.896.897-.707.707-.897-.896a.5.5 0 1 1 .708-.708"/>
      </svg>
      case 'sent':
        return <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-check2 status-icon sent" viewBox="0 0 16 16">
        <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0"/>
      </svg>
      default:
        return null;
    }
  };

  // Update conversation name
  const handleUpdateConversationName = async () => {
    if (!editedName.trim() || !selectedConversation) return;
    setSavingName(true);
    try {
      const { error } = await supabase
        .from('whatsapp_conversations')
        .update({ contact_name: editedName.trim() })
        .eq('id', selectedConversation.id);
      if (error) throw error;
      setSelectedConversation(prev => ({ ...prev, contact_name: editedName.trim() }));
      setConversations(prev => prev.map(c => c.id === selectedConversation.id ? { ...c, contact_name: editedName.trim() } : c));
    } catch (err) {
      console.error('Failed to update conversation name:', err);
    }
    setSavingName(false);
    setEditingName(false);
  };

  const filteredConversations = conversations.filter(conv => {
    const matchesSearch = conv.contact_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           conv.phone_number?.includes(searchTerm);
    const matchesFilter = filterType === 'all' || (filterType === 'unread' && conv.unread_count > 0);
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="whatsapp-chats-container">
      {/* Sidebar - Conversation List */}
      <div className={`chats-sidebar ${selectedConversation ? 'chat-open' : ''}`}>
        {/* Search Bar */}
        <div className="sidebar-header">
          <div className="search-container">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search chats..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          {/* Filter Buttons */}
          <div className="filter-container">
            <button
              className={`filter-button ${filterType === 'all' ? 'active' : ''}`}
              onClick={() => setFilterType('all')}
            >
              All
            </button>
            <button
              className={`filter-button ${filterType === 'unread' ? 'active' : ''}`}
              onClick={() => setFilterType('unread')}
            >
              Unread
            </button>
          </div>
        </div>

        {/* Conversations List */}
        <div className="conversations-list">
          {loading ? (
            <div className="loading-conversations">
              <div className="spinner"></div>
              <p>Loading chats...</p>
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="no-conversations">
              <p>No chats found</p>
            </div>
          ) : (
            filteredConversations.map((conv) => (
              <div
                key={conv.id}
                className={`conversation-item ${selectedConversation?.id === conv.id ? 'active' : ''}`}
                onClick={() => setSelectedConversation(conv)}
              >
                <div className="conversation-avatar">
                  {conv.contact_name?.charAt(0) || '?'}
                </div>
                <div className="conversation-info">
                  <div className="conversation-header">
                    <h4>{conv.contact_name || conv.phone_number}</h4>
                    <span className="conversation-time">
                      {conv.last_message_at && formatDate(conv.last_message_at)}
                    </span>
                  </div>
                  <div className="conversation-footer">
                    <p className="last-message">
                      {conv.last_message_text?.startsWith('[Unsupported message type: reaction]') 
                        ? 'Reacted to a message' 
                        : (conv.last_message_text || 'No messages yet')}
                    </p>
                    {conv.unread_count > 0 && (
                      <span className="unread-badge">{conv.unread_count}</span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Chat Window */}
      <div className={`chat-window ${selectedConversation ? 'chat-open' : ''}`}>
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="chat-header">
              <div className="chat-header-info">
                <button className="mobile-back-btn" onClick={() => setSelectedConversation(null)}>
                  <FaArrowLeft />
                </button>
                <div className="chat-avatar">
                  {selectedConversation.contact_name?.charAt(0) || '?'}
                </div>
                <div className="chat-title">
                  {editingName ? (
                    <div className="chat-name-edit-row">
                      <input
                        ref={nameInputRef}
                        className="chat-name-edit-input"
                        value={editedName}
                        onChange={(e) => setEditedName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleUpdateConversationName();
                          if (e.key === 'Escape') { setEditingName(false); setSavingName(false); }
                        }}
                        onBlur={handleUpdateConversationName}
                        autoFocus
                        disabled={savingName}
                      />
                      {savingName && <span className="chat-name-saving-spinner" />}
                    </div>
                  ) : (
                    <div className="chat-name-row">
                      <h3>{selectedConversation.contact_name || selectedConversation.phone_number}</h3>
                      {savingName ? (
                        <span className="chat-name-saving-spinner" />
                      ) : (
                        <button
                          className="chat-edit-name-btn"
                          onClick={() => {
                            setEditedName(selectedConversation.contact_name || '');
                            setEditingName(true);
                          }}
                          title="Edit name"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg>
                        </button>
                      )}
                    </div>
                  )}
                  <span className="chat-phone">{selectedConversation.phone_number}</span>
                </div>
              </div>
              <button className="chat-menu-btn">
                <FaEllipsisV />
              </button>
            </div>

            {/* Messages Area */}
            <div className="messages-area">
              {messages.length === 0 ? (
                <div className="no-messages">
                  <p>No messages yet</p>
                  <span>Start the conversation</span>
                </div>
              ) : (
                messages.map((msg, index) => {
                  const showDate = index === 0 || 
                    new Date(messages[index - 1].sent_at).toDateString() !== new Date(msg.sent_at).toDateString();

                  return (
                    <React.Fragment key={msg.id}>
                      {showDate && (
                        <div className="date-separator">
                          <span>{formatDate(msg.sent_at)}</span>
                        </div>
                      )}
                      {msg.message_type === 'reaction' ? (
                        <div className={`message ${msg.direction} reaction-message`}>
                          <div className="reaction-bubble">
                            <span className="reaction-emoji">{msg.message_text}</span>
                            <span className="reaction-time">{formatTime(msg.sent_at)}</span>
                          </div>
                        </div>
                      ) : (
                        <div className={`message ${msg.direction}`}>
                          <div className="message-bubble">
                            {msg.media_url && msg.message_type === 'image' && (
                              <div 
                                className="message-image" 
                                onClick={() => handleImageClick(msg.media_url)}
                                style={{ cursor: 'pointer' }}
                                title="Click to view full size"
                              >
                                <img src={msg.media_url} alt="Shared image" />
                              </div>
                            )}
                            {msg.media_url && (msg.message_type === 'audio' || msg.message_type === 'voice') && (
                              <div className="message-audio">
                                <audio controls preload="metadata">
                                  <source src={msg.media_url} />
                                  Your browser does not support audio playback.
                                </audio>
                              </div>
                            )}
                            {msg.media_url && msg.message_type === 'video' && (
                              <div className="message-video">
                                <video controls preload="metadata" playsInline>
                                  <source src={msg.media_url} />
                                  Your browser does not support video playback.
                                </video>
                              </div>
                            )}
                            {msg.media_url && msg.message_type === 'document' && (
                              <a
                                href={msg.media_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="message-document"
                                title="Click to view/download"
                              >
                                <span className="doc-icon">📄</span>
                                <span className="doc-name">{msg.media_filename || 'Document'}</span>
                                <span className="doc-download">⬇</span>
                              </a>
                            )}
                            {msg.message_text && (
                              <p className="message-text">{msg.message_text}</p>
                            )}
                            <div className="message-footer">
                              <span className="message-time">{formatTime(msg.sent_at)}</span>
                              {msg.direction === 'outgoing' && getStatusIcon(msg.status)}
                            </div>
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input - 24-Hour Window Logic */}
            <div className={`message-input-area ${!canReply ? 'disabled' : ''}`}>
              {canReply && windowTimeRemaining && (
                <div className="window-timer">
                  <span className="timer-icon">⏰</span>
                  <span className="timer-text">
                    {windowTimeRemaining < 3600000 ? (
                      <span style={{color: '#ff4444'}}>Expiring soon: {formatWindowTime(windowTimeRemaining)}</span>
                    ) : (
                      <span>Reply window: {formatWindowTime(windowTimeRemaining)} remaining</span>
                    )}
                  </span>
                </div>
              )}
              
              {selectedFile && (
                <div className="selected-file">
                  <span>📄 {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)</span>
                  <button onClick={() => { setSelectedFile(null); fileInputRef.current.value = ''; }}>✕</button>
                </div>
              )}
              
              <div className="input-wrapper">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept="image/*,.pdf,.doc,.docx"
                  style={{ display: 'none' }}
                />
                <button 
                  className="attach-btn" 
                  disabled={!canReply || sending}
                  onClick={() => fileInputRef.current?.click()}
                  title="Attach file"
                >
                  <FaImage />
                </button>
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !sending && handleSendMessage()}
                  placeholder={canReply ? "Type a message..." : "24-hour window expired - Use Bulk Notice"}
                  disabled={!canReply || sending}
                  className="message-input"
                />
                <button 
                  className="send-btn" 
                  disabled={!canReply || sending || (!newMessage.trim() && !selectedFile)}
                  onClick={handleSendMessage}
                >
                  {sending ? '⏳' : <FaPaperPlane />}
                </button>
              </div>
              
              {!canReply && (
                <p className="input-note warning">
                  ⚠️ 24-hour reply window expired. Use "Bulk Notice" to send template messages.
                </p>
              )}
            </div>
          </>
        ) : (
          <div className="no-chat-selected">
            <div className="no-chat-icon">💬</div>
            <h3>WhatsApp Business Chat</h3>
            <p>Select a conversation to view messages</p>
            <p className="note">This interface shows incoming messages from parents</p>
          </div>
        )}
      </div>

      {/* Image Preview Modal */}
      {imageModalOpen && (
        <div className="image-modal-overlay" onClick={closeImageModal}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="image-modal-close" onClick={closeImageModal}>
              ✕
            </button>
            <img src={selectedImage} alt="Full size preview" />
          </div>
        </div>
      )}
    </div>
  );
};

export default Chats;
